import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteShell from "../../components/layout/SiteShell";
import SEO from "../../components/seo/SEO";
import ConversionCtas from "../../components/content/ConversionCtas";
import DarkCard from "../../components/layout/DarkCard";
import LifestyleImageCard from "../../components/shared/LifestyleImageCard";
import { getLifestyleImage } from "../../lib/lifestyleImages";
import { HOME_CTA_DARK_OUTLINE, HOME_CTA_DARK_PRIMARY } from "../../lib/homeCta";
import { BRAND_BANNER_IMAGE } from "../../lib/data";

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const featured = [
  {
    to: "/mix-ins",
    title: "Sea Moss Mix-Ins",
    desc: "Ginger, turmeric, cinnamon and other common pairings — routine inspiration, not medical advice.",
    imageKey: "mixInsHero",
  },
  {
    to: "/learn/gold-vs-purple-sea-moss",
    title: "Gold vs Purple",
    desc: "How the two Moss Matrix bases differ in colour, habit, and kitchen use.",
    imageKey: "routineKitchen",
  },
  {
    to: "/learn/how-to-use-sea-moss-gel",
    title: "How to Use Sea Moss",
    desc: "Smoothies, oats, juice, tea, and practical ideas for daily routines.",
    imageKey: "routineSmoothie",
  },
  {
    to: "/learn/how-to-store-sea-moss-gel",
    title: "Storage and Delivery",
    desc: "Fridge-first tips, clean spoons, and how UK fresh gel orders are handled.",
    imageKey: "packingOrders",
  },
  {
    to: "/learn/sea-moss-for-active-routines",
    title: "Sea Moss for Active Routines",
    desc: "Fridge-ready habits for days you already move — kitchen, coastal, calm.",
    imageKey: "routineCoastal",
  },
  {
    to: "/learn/sea-moss-benefits",
    title: "Commonly Researched Sea Moss Topics",
    desc: "What people commonly research about sea moss — calm education for a food product.",
    imageKey: "routineMorning",
  },
];

const moreLinks = [
  {
    to: "/learn/sea-moss-nutrition",
    title: "Nutrition resources",
    desc: "Trace minerals, amino acids, and calm educational framing.",
  },
  { to: "/ingredients", title: "Ingredient Library", desc: "Browse botanicals people commonly research with sea moss." },
  { to: "/learn/what-is-sea-moss", title: "What is sea moss?", desc: "A plain-English intro to what you are buying." },
  { to: "/reviews", title: "Marketplace Feedback", desc: "Buyer comments, clearly labelled by source." },
  { to: "/learn/sea-moss-research", title: "Sea moss research", desc: "Evidence snapshot with real citations and reading links." },
  { to: "/community-reward", title: "Routine Reward", desc: "Order 3 times. Get a free 300ml Gold jar." },
];

export default function LearnHubPage() {
  const hero = getLifestyleImage("heroLifestyle");

  return (
    <SiteShell theme="dark">
      <SEO
        title="Learn | Sea moss guides and practical tips | Moss Matrix"
        description="Moss Matrix learn hub: mix-ins, Gold vs Purple, how to use and store gel, active routines, and commonly researched sea moss topics. Calm education for a food product."
        path="/learn"
      />

      <header className="relative min-h-[52svh] md:min-h-[58svh] w-full overflow-hidden border-b border-white/10">
        <img
          src={hero.src}
          alt=""
          width={hero.width}
          height={hero.height}
          className="absolute inset-0 h-full w-full object-cover brightness-[1.12] contrast-[1.02] saturate-[1.04]"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-[#0c1410]/62 via-[#0c1410]/35 to-[#0c1410]/22"
          aria-hidden
        />
        <div
          className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#0c1410] via-[#0c1410]/50 to-transparent"
          aria-hidden
        />
        <div className="relative z-10 flex min-h-[52svh] md:min-h-[58svh] flex-col justify-end px-6 md:px-10 pb-12 md:pb-14 pt-24">
          <div className="max-w-7xl mx-auto w-full">
            <p className="home-eyebrow m-0 mb-3 text-sage/95">Learn</p>
            <h1 className="font-sans font-semibold text-4xl md:text-5xl lg:text-6xl text-ivory m-0 tracking-tight max-w-2xl leading-[1.08]">
              Guides for a clearer routine.
            </h1>
            <p className="mt-4 text-base md:text-lg text-sage/95 m-0 max-w-xl leading-relaxed">
              Short reads on active habits, Gold vs Purple, mix-ins, and how to use gel — without miracle language.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/learn/sea-moss-for-active-routines" className={`${HOME_CTA_DARK_PRIMARY} px-7 py-3 text-sm`}>
                Active routines
              </Link>
              <Link to="/learn/how-to-use-sea-moss-gel" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3 text-sm`}>
                How to use
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-12 md:pt-16 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 mm-dark-content">
          <motion.div {...fadeUp} className="mb-12 md:mb-14">
            <figure className="m-0 max-w-4xl mx-auto">
              <div className="overflow-hidden rounded-[1.25rem] ring-1 ring-white/15 bg-[#f7f3eb] shadow-[0_24px_56px_-32px_rgba(0,0,0,0.4)]">
                <img
                  src={BRAND_BANNER_IMAGE}
                  alt="Moss Matrix — Pure. Raw. Wildcrafted. From Nature to You"
                  className="block w-full h-auto"
                  width={1600}
                  height={900}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <figcaption className="mt-3 text-center text-xs text-sage/80 m-0 leading-relaxed">
                Pure. Raw. Wildcrafted. — brand story creative (moved off the homepage so Shop stays order-first).
              </figcaption>
            </figure>
          </motion.div>

          <motion.div {...fadeUp}>
            <h2 className="font-sans font-semibold text-xl md:text-2xl text-ivory m-0 mb-6">Featured guides</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
              {featured.map((a) => {
                const img = getLifestyleImage(a.imageKey);
                return (
                  <Link key={`${a.to}-${a.title}`} to={a.to} className="group block transition-transform duration-500 hover:-translate-y-1">
                    <LifestyleImageCard
                      src={img.src}
                      alt={img.alt}
                      placeholderLabel={img.placeholderLabel}
                      title={a.title}
                      copy={a.desc}
                      width={img.width}
                      height={img.height}
                      tone="bright"
                      aspectClass="aspect-[4/5] sm:aspect-[5/6] min-h-[220px]"
                    />
                  </Link>
                );
              })}
            </div>
          </motion.div>

          <motion.div {...fadeUp}>
            <h2 className="font-sans font-semibold text-xl md:text-2xl text-ivory m-0 mb-6">More to explore</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
              {moreLinks.map((a) => (
                <Link key={a.to} to={a.to} className="group block h-full">
                  <DarkCard className="h-full flex flex-col transition duration-300 group-hover:border-gold/40 group-hover:-translate-y-0.5">
                    <h3 className="font-sans font-semibold text-xl text-ivory m-0 group-hover:text-goldhover transition">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-sm text-sage leading-relaxed flex-1 m-0">{a.desc}</p>
                    <span className="mt-5 text-xs tracking-widest uppercase text-sage/80 group-hover:text-gold transition">
                      Read →
                    </span>
                  </DarkCard>
                </Link>
              ))}
            </div>
          </motion.div>

          <ConversionCtas />

          <p className="text-sm text-sage text-center mt-10">
            Ready to order?{" "}
            <Link to="/shop" className="underline hover:text-goldhover">
              Shop Moss Matrix
            </Link>
            , explore{" "}
            <Link to="/mix-ins" className="underline hover:text-goldhover">
              mix-ins
            </Link>
            , or browse the{" "}
            <Link to="/ingredients" className="underline hover:text-goldhover">
              ingredient library
            </Link>
            .
          </p>
        </div>
      </main>
    </SiteShell>
  );
}
